import { emptyCardStackExtracted } from "@shared/lib/card-stack/extracted"
import { emptyProseMirrorContent } from "@shared/lib/card-stack/prose-mirror-utils"
import { isUnifiedCardStack } from "@shared/lib/card-stack/unified-migration"
import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonDelete } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { CardStack } from "@shared/models/card-stack"
import type { PatchModel } from "@shared/models/patch"
import Patch from "@shared/models/patch-updater"
import { partial } from "lodash"
import { cloneDeep } from "lodash-es"
import type { PreparedAction } from "../../action-doc-builder"
import type { Narrative } from "../create-narrative"
import defaultLessonActivity from "./default-lesson-activity"
import defaultLessonContext from "./default-lesson-context"
import defaultLessonModels from "./default-lesson-models"

interface Payload {
  cardStack: CardStack
}

export function prepareLessonClear(payload: Payload): PreparedAction {
  return {
    name: LESSON_CLEAR.name,
    payload: payload,
    patches: LESSON_CLEAR.patches(payload),
    undoPatches: LESSON_CLEAR.undoPatches(payload),
    narrative: partial(LESSON_CLEAR.narrative, payload),
  }
}

export const LESSON_CLEAR = {
  name: "LESSON_CLEAR",

  patches(payload: Payload): Array<PatchModel> {
    let patch = Patch.create("card-stack", payload.cardStack.id)
      .set("attributes.title", "")
      .set("attributes.cards", [])
      .set("attributes.parentCardStack", null)
    if (isUnifiedCardStack(payload.cardStack)) {
      patch
        .set("attributes.proseMirror", emptyProseMirrorContent())
        .set("attributes.extracted", emptyCardStackExtracted())
    }
    let patchForCardStackSummary = Patch.create("card-stack-summary", payload.cardStack.id).set(
      "attributes.title",
      "",
    )
    return [patch.patch, patchForCardStackSummary.patch]
  },

  undoPatches(payload: Payload): Array<PatchModel> {
    let cardStack = payload.cardStack
    let patch = Patch.create("card-stack", payload.cardStack.id)
      .set("attributes.title", cardStack.attributes.title)
      .set("attributes.cards", cloneDeep(cardStack.attributes.cards))
      .set("attributes.parentCardStack", cloneDeep(cardStack.attributes.parentCardStack ?? null))
    if (isUnifiedCardStack(cardStack)) {
      patch
        .set("attributes.proseMirror", cloneDeep(cardStack.attributes.proseMirror))
        .set("attributes.extracted", cloneDeep(cardStack.attributes.extracted))
    }
    let patchForCardStackSummary = Patch.create("card-stack-summary", payload.cardStack.id)
    patchForCardStackSummary.set("attributes.title", cardStack.attributes.title)
    return [patch.patch, patchForCardStackSummary.patch]
  },

  narrative(payload: Payload, findFn: FindFn, userId: string): Narrative | null {
    let lessonTitle = payload.cardStack.attributes.title
    let models = defaultLessonModels({ cardStackId: payload.cardStack.id }, findFn, userId)
    let newEvent: LessonDelete = {
      ...createBlankEvent(),
      root_id: models.planbook?.id ?? null,
      root_type: "planbook",
      primary_object_id: payload.cardStack.id,
      date_plan_scheduled_for: models.dateStringForCardStack,
      primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
      primary_verb: "delete",
      primary_possessive: getPossessiveForPlanbook(findFn, userId, models.planbook?.id),
      primary_object_type: "lesson",
      primary_object_specifier: null,
      adverbial_verb: "removing",
      adverbial_object_type: "lesson",
      adverbial_object_specifier: "entire_object",
      adverbial_object_id: null,
      adverbial_object_id_and_ancestor_ids: null,
    }

    return {
      title: "Deleted a lesson",
      titleForAnalytics: "Lesson - Delete",
      description: `Deleted "${lessonTitle}"`,
      context: defaultLessonContext(payload, models),
      activity: defaultLessonActivity(payload, models, "deleted"),
      newAnalyticsEvents: [newEvent],
    }
  },
}
export default LESSON_CLEAR
